/**
 * AssessOS Field — Schema canonico de plantillas de survey.
 *
 * Fuente de verdad: docs/discovery/02-arquitectura.md secciones 8.3 y 8.4.
 * Cualquier campo nuevo debe agregarse aqui Y documentarse en
 * docs/template-schema.md (bloque G del Sprint 2).
 *
 * Decisiones de diseno (ADR-004):
 *   - El schema vive como JSONB en la columna `survey_templates.schema`.
 *   - Se valida con Zod en publicacion; no se publica si Zod falla.
 *   - Plantilla publicada es INMUTABLE; cambios → nueva fila version+1.
 *   - El frontend renderiza dinamicamente leyendo este mismo tipo.
 *
 * Tipos de campo soportados (discovery seccion 8.4 + Sprint 30):
 *   text, textarea, number, single_select, multi_select, boolean, date,
 *   score_1_5, maturity_scale (S30), photo, signature.
 */
import { z } from 'zod';

import { FRAMEWORK_ID_REGEX } from './frameworks.js';

// ─────────────────────────────────────────────────────────────────────
// Identificadores
// ─────────────────────────────────────────────────────────────────────

/** code de plantilla: minusculas, digitos y guion bajo. */
export const TemplateCodeSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, 'code: minusculas, digitos, guion bajo; empieza con letra');

/** id de seccion / pregunta dentro de una plantilla (referencias). */
export const NodeIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, 'id: alfanumerico + guion bajo, empieza con letra');

// ─────────────────────────────────────────────────────────────────────
// Validaciones por tipo de campo
// ─────────────────────────────────────────────────────────────────────

const TextValidationSchema = z
  .object({
    minLength: z.number().int().nonnegative().optional(),
    maxLength: z.number().int().positive().optional(),
    regex: z.string().optional(),
  })
  .strict();

const NumberValidationSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    integer: z.boolean().optional(),
  })
  .strict();

const DateValidationSchema = z
  .object({
    min: z.string().optional(),
    max: z.string().optional(),
  })
  .strict();

// ─────────────────────────────────────────────────────────────────────
// Trazabilidad de controles (ADR-012)
// ─────────────────────────────────────────────────────────────────────

/**
 * Identificador de framework. La regex se importa de `frameworks.ts`
 * para que el catalogo y el validador no diverjan.
 *
 * Ejemplos validos: `ISO27001:2022`, `NIST_CSF:2.0`, `CIS:v8.1`,
 * `OEA_RD:ZF`, `BPM:GENERAL`.
 */
export const FrameworkIdSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(FRAMEWORK_ID_REGEX, 'framework: MAYUSCULAS:VERSION (ej. ISO27001:2022)');

/**
 * Referencia a un control externo: marco + id del control + descripcion
 * opcional. Una pregunta puede declarar hasta 20 controles.
 *
 * El campo `description` es texto libre hasta 500 chars; usar para
 * citar el titulo del control o un extracto operativo de la norma.
 */
export const ControlRefSchema = z
  .object({
    framework: FrameworkIdSchema,
    id: z.string().min(1).max(80),
    description: z.string().max(500).optional(),
  })
  .strict();

export type ControlRef = z.infer<typeof ControlRefSchema>;

// ─────────────────────────────────────────────────────────────────────
// Prefill de auditoría desde captura cliente (ADR-040)
// ─────────────────────────────────────────────────────────────────────

/**
 * Transformaciones soportadas al copiar un valor del cliente al auditor.
 *
 *   identity     — copia el valor tal cual (requiere que los tipos sean
 *                  compatibles; si no, se descarta y registra en skippedRefs).
 *   option_remap — usa `optionMap[valorCliente]` para traducir entre opciones
 *                  de select distintos. Requiere `optionMap`.
 *   to_boolean   — convierte valor truthy (string no-vacío, número ≠ 0) a true/false.
 *   join_text    — multi_select cliente → text auditor (valores unidos con ", ").
 *   number_to_date — reservado; nunca copia directa (siempre se descarta con skip).
 */
const PrefillTransform = z.enum([
  'identity',
  'number_to_date',
  'option_remap',
  'to_boolean',
  'join_text',
]);
export type PrefillTransform = z.infer<typeof PrefillTransform>;

/**
 * Instrucción de prefill de una pregunta del auditor: de qué ref del
 * cliente viene el valor y cómo transformarlo. Co-located en el JSON
 * del auditor según ADR-040, D2.
 */
export const ClientPrefillSchema = z
  .object({
    /** Ref (id de pregunta) en la plantilla *cliente* de donde leer el valor. */
    ref: NodeIdSchema,
    /**
     * Transformación a aplicar. Por defecto `identity`.
     * Documentada en ADR-040 D3.
     */
    transform: PrefillTransform.default('identity'),
    /**
     * Solo para `option_remap`: mapa valorCliente → valorAuditor.
     * Si el valor del cliente no tiene entrada en el mapa, se descarta
     * (no se prellenan valores; campo queda editable).
     */
    optionMap: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  })
  .strict();

export type ClientPrefill = z.infer<typeof ClientPrefillSchema>;

export const PrefillSchema = z.object({ fromClient: ClientPrefillSchema }).strict();
export type Prefill = z.infer<typeof PrefillSchema>;

// ─────────────────────────────────────────────────────────────────────
// Citas bibliograficas (Sprint 17)
// ─────────────────────────────────────────────────────────────────────

/**
 * Referencia a una fuente secundaria que sustenta la pregunta de auditoria:
 * guias de implantacion (NQA), portales tecnicos (iso-info.net), texto
 * oficial de la norma, etc. Se distingue de `controls`:
 *   - `controls` mapea la pregunta a un control normativo (ISO27001 A.5.1,
 *     NIST CSF GV.OC-01, etc.) — input para la matriz de cobertura.
 *   - `sources` cita el material consultado para redactar la pregunta —
 *     input para que el auditor y el cliente vean la fuente al auditar.
 *
 * `source` se mantiene como string libre con minimo de 2 chars hasta que
 * tengamos un catalogo cerrado (planeado S17-E). Convencion sugerida en
 * MAYUSCULAS, ej. `NQA`, `ISO_OFICIAL`, `ISO_INFO`.
 */
export const SourceRefSchema = z
  .object({
    source: z.string().min(2).max(40),
    citation: z.string().min(1).max(300),
    url: z.string().url().max(500).optional(),
  })
  .strict();

export type SourceRef = z.infer<typeof SourceRefSchema>;

// ─────────────────────────────────────────────────────────────────────
// Question — discriminated union por `type`
// ─────────────────────────────────────────────────────────────────────

const BaseQuestionSchema = z.object({
  id: NodeIdSchema,
  label: z.string().min(1).max(500),
  description: z.string().max(2000).optional(),
  required: z.boolean().optional(),
  /**
   * Dominios de scoring que esta pregunta puede afectar via reglas.
   * Documentacion solamente; el motor de scoring usa las reglas en
   * `schema.scoring.rules`.
   */
  affectsDomains: z.array(NodeIdSchema).optional(),
  /**
   * ADR-012: trazabilidad a controles externos (ISO 27001, NIST CSF,
   * CIS, regulaciones RD, etc.). Opcional y retro-compatible — plantillas
   * existentes sin el campo siguen validando. Limite de 20 controles
   * por pregunta para evitar ruido editorial.
   */
  controls: z.array(ControlRefSchema).max(20).optional(),
  /**
   * Sprint 17: fuentes bibliograficas que sustentan la pregunta (guias
   * NQA, ISO-info, etc.). Opcional y retro-compatible. Distinta de
   * `controls` (mapeo normativo) — ver `SourceRefSchema`.
   */
  sources: z.array(SourceRefSchema).max(20).optional(),
  /**
   * ADR-040: instrucción de prefill desde la captura cliente. Solo
   * presente en plantillas *auditor*; las plantillas *cliente* nunca
   * lo tienen. Opcional y retro-compatible — plantillas sin el campo
   * siguen validando (.strict() lo declara aquí en lugar de rechazarlo).
   */
  prefill: PrefillSchema.optional(),
});

const TextQuestion = BaseQuestionSchema.extend({
  type: z.literal('text'),
  validation: TextValidationSchema.optional(),
});

const TextareaQuestion = BaseQuestionSchema.extend({
  type: z.literal('textarea'),
  validation: TextValidationSchema.optional(),
});

const NumberQuestion = BaseQuestionSchema.extend({
  type: z.literal('number'),
  validation: NumberValidationSchema.optional(),
  unit: z.string().max(20).optional(),
});

const OptionPrimitive = z.union([z.string().min(1), z.number()]);
const OptionObject = z
  .object({
    value: OptionPrimitive,
    label: z.string().min(1),
  })
  .strict();
const SelectOption = z.union([OptionPrimitive, OptionObject]);

const SingleSelectQuestion = BaseQuestionSchema.extend({
  type: z.literal('single_select'),
  options: z.array(SelectOption).min(2),
});

const MultiSelectQuestion = BaseQuestionSchema.extend({
  type: z.literal('multi_select'),
  options: z.array(SelectOption).min(2),
  minSelections: z.number().int().nonnegative().optional(),
  maxSelections: z.number().int().positive().optional(),
});

const BooleanQuestion = BaseQuestionSchema.extend({
  type: z.literal('boolean'),
  /** Mostrar opcion 'N/A' ademas de Si/No. */
  allowNotApplicable: z.boolean().optional(),
});

const DateQuestion = BaseQuestionSchema.extend({
  type: z.literal('date'),
  validation: DateValidationSchema.optional(),
});

const Score1to5Question = BaseQuestionSchema.extend({
  type: z.literal('score_1_5'),
  /** Etiquetas para 1 y 5 (ej. "Pesimo" / "Excelente"). */
  scaleLabels: z
    .object({ low: z.string().max(50), high: z.string().max(50) })
    .strict()
    .optional(),
});

/**
 * Sprint 30: maturity_scale. Variante enriquecida de score_1_5 para
 * evaluaciones de madurez tipo CMMI / NIST CSF 2.0 PR.AT / COBIT 2019
 * Capability Levels. A diferencia de score_1_5, cada nivel tiene un
 * nombre (ej. "Initial") y un descriptor concreto ("Procesos ad-hoc,
 * sin documentar").
 *
 * Si se omite `levels`, se aplica el preset CMMI:
 *   1 Initial / 2 Managed / 3 Defined / 4 Quantitatively Managed / 5 Optimizing
 *
 * Habilita renderizacion de radar charts + heatmaps de cobertura sin
 * romper plantillas existentes basadas en boolean/single_select.
 */
const MaturityLevel = z
  .object({
    /** 1-5 (CMMI/CSF Tiers) o 0-4 (NIST CSF Tiers Partial-Tier4). */
    value: z.number().int().min(0).max(5),
    /** Nombre corto (ej. "Defined", "Risk Informed"). */
    label: z.string().min(1).max(50),
    /** Descripcion verificable del nivel (criterios para alcanzarlo). */
    descriptor: z.string().min(1).max(500),
  })
  .strict();

const MaturityScaleQuestion = BaseQuestionSchema.extend({
  type: z.literal('maturity_scale'),
  /**
   * Niveles de madurez personalizables. Si se omite, se aplica preset
   * CMMI (Initial/Managed/Defined/Quantitatively Managed/Optimizing).
   * Debe contener entre 3 y 6 niveles ordenados por `value` ascendente.
   */
  levels: z.array(MaturityLevel).min(3).max(6).optional(),
  /**
   * Framework de origen del modelo de madurez. Solo etiqueta UI; la
   * autoridad citable real va en `controls[]` y `sources[]`.
   */
  maturityModel: z
    .enum(['CMMI', 'NIST_CSF_TIERS', 'COBIT_CAPABILITY', 'ISO_15504', 'CUSTOM'])
    .optional(),
  /**
   * Nivel objetivo declarado por la entidad (para calcular brecha en
   * `insights/`). Opcional — si se omite, no se calcula gap.
   */
  targetLevel: z.number().int().min(0).max(5).optional(),
});

const PhotoQuestion = BaseQuestionSchema.extend({
  type: z.literal('photo'),
  /** Limite de fotos para esta pregunta especifica. Default 1. */
  maxPhotos: z.number().int().positive().max(50).optional(),
});

const SignatureQuestion = BaseQuestionSchema.extend({
  type: z.literal('signature'),
});

export type MaturityLevelDef = z.infer<typeof MaturityLevel>;

/** Preset CMMI estandar — 5 niveles 1-5. */
export const CMMI_LEVELS: readonly MaturityLevelDef[] = [
  { value: 1, label: 'Initial', descriptor: 'Procesos ad-hoc, impredecibles, sin documentar' },
  { value: 2, label: 'Managed', descriptor: 'Procesos por proyecto, gestionados reactivamente' },
  {
    value: 3,
    label: 'Defined',
    descriptor: 'Procesos estandarizados, documentados a nivel organizacional',
  },
  {
    value: 4,
    label: 'Quantitatively Managed',
    descriptor: 'Procesos medidos y controlados con metricas cuantitativas',
  },
  {
    value: 5,
    label: 'Optimizing',
    descriptor: 'Mejora continua basada en datos, innovacion sistematica',
  },
] as const;

/** Preset NIST CSF 2.0 Implementation Tiers — 4 niveles 1-4. */
export const NIST_CSF_TIERS: readonly MaturityLevelDef[] = [
  {
    value: 1,
    label: 'Partial',
    descriptor: 'Practicas de gestion de riesgo ad-hoc; conciencia limitada',
  },
  {
    value: 2,
    label: 'Risk Informed',
    descriptor: 'Practicas aprobadas pero no establecidas como politica formal',
  },
  {
    value: 3,
    label: 'Repeatable',
    descriptor: 'Politicas formales aprobadas y aplicadas consistentemente',
  },
  {
    value: 4,
    label: 'Adaptive',
    descriptor: 'Adaptacion continua basada en leciones aprendidas y predictivos',
  },
] as const;

export const QuestionSchema = z.discriminatedUnion('type', [
  TextQuestion,
  TextareaQuestion,
  NumberQuestion,
  SingleSelectQuestion,
  MultiSelectQuestion,
  BooleanQuestion,
  DateQuestion,
  Score1to5Question,
  MaturityScaleQuestion,
  PhotoQuestion,
  SignatureQuestion,
]);

export type Question = z.infer<typeof QuestionSchema>;
export type QuestionType = Question['type'];

// ─────────────────────────────────────────────────────────────────────
// Section
// ─────────────────────────────────────────────────────────────────────

export const SectionSchema = z
  .object({
    id: NodeIdSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    /** Orden de presentacion. Si se omite, vale el indice del array. */
    order: z.number().int().nonnegative().optional(),
    questions: z.array(QuestionSchema).min(1),
  })
  .strict();

export type Section = z.infer<typeof SectionSchema>;

// ─────────────────────────────────────────────────────────────────────
// Scoring — dominios + reglas declarativas
// ─────────────────────────────────────────────────────────────────────

export const ScoreDomainSchema = z
  .object({
    id: NodeIdSchema,
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    /** Peso en el score agregado. Sumatoria de pesos debe = 1.0 (ver refine global). */
    weight: z.number().min(0).max(1),
  })
  .strict();

export type ScoreDomain = z.infer<typeof ScoreDomainSchema>;

/**
 * Severidad cualitativa del finding cuando una regla dispara.
 * Mapeo a delta de score lo decide el motor (Sprint 2 bloque C).
 */
export const FindingSeveritySchema = z.enum(['critical', 'high', 'medium', 'low', 'info']);
export type FindingSeverity = z.infer<typeof FindingSeveritySchema>;

export const ScoringRuleSchema = z
  .object({
    id: NodeIdSchema,
    description: z.string().max(500).optional(),
    /**
     * Expresion del DSL acotado (no eval). Tiene acceso a `responses.q_X_Y`.
     * Operadores soportados: ==, !=, <, <=, >, >=, &&, ||, !, includes(),
     * length(). Documentacion completa en docs/template-schema.md.
     */
    when: z.string().min(1).max(500),
    then: z
      .object({
        domain: NodeIdSchema,
        delta: z.number().min(-100).max(100),
        finding: z.string().max(500).optional(),
        severity: FindingSeveritySchema.optional(),
      })
      .strict(),
  })
  .strict();

export type ScoringRule = z.infer<typeof ScoringRuleSchema>;

export const ScoringSchema = z
  .object({
    domains: z.array(ScoreDomainSchema).min(1),
    /** Score base por dominio antes de aplicar reglas (default 100). */
    baseScore: z.number().min(0).max(100).optional(),
    rules: z.array(ScoringRuleSchema).default([]),
  })
  .strict()
  .superRefine((scoring, ctx) => {
    // 1) IDs de dominios unicos
    const ids = scoring.domains.map((d) => d.id);
    const dups = ids.filter((id, i) => ids.indexOf(id) !== i);
    if (dups.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Dominios con id duplicado: ${[...new Set(dups)].join(', ')}`,
        path: ['domains'],
      });
    }
    // 2) Pesos suman 1.0 (tolerancia de 0.01)
    const sum = scoring.domains.reduce((acc, d) => acc + d.weight, 0);
    if (Math.abs(sum - 1) > 0.01) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `La sumatoria de pesos de dominios debe ser 1.0; obtenida: ${sum.toFixed(3)}`,
        path: ['domains'],
      });
    }
    // 3) Cada regla referencia un dominio existente
    const domainIds = new Set(ids);
    scoring.rules.forEach((rule, i) => {
      if (!domainIds.has(rule.then.domain)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Regla "${rule.id}" referencia dominio inexistente "${rule.then.domain}"`,
          path: ['rules', i, 'then', 'domain'],
        });
      }
    });
  });

export type Scoring = z.infer<typeof ScoringSchema>;

// ─────────────────────────────────────────────────────────────────────
// SurveyTemplate — el documento completo
// ─────────────────────────────────────────────────────────────────────

export const SurveyTemplateSchema = z
  .object({
    code: TemplateCodeSchema,
    version: z.number().int().positive(),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    sections: z.array(SectionSchema).min(1),
    scoring: ScoringSchema,
  })
  .strict()
  .superRefine((tpl, ctx) => {
    // 1) IDs de seccion unicos
    const sectionIds = tpl.sections.map((s) => s.id);
    const dupSec = sectionIds.filter((id, i) => sectionIds.indexOf(id) !== i);
    if (dupSec.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Secciones con id duplicado: ${[...new Set(dupSec)].join(', ')}`,
        path: ['sections'],
      });
    }
    // 2) IDs de pregunta unicos GLOBALMENTE en la plantilla
    const allQuestionIds: string[] = [];
    tpl.sections.forEach((s) => {
      s.questions.forEach((q) => allQuestionIds.push(q.id));
    });
    const dupQ = allQuestionIds.filter((id, i) => allQuestionIds.indexOf(id) !== i);
    if (dupQ.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Preguntas con id duplicado entre secciones: ${[...new Set(dupQ)].join(', ')}`,
        path: ['sections'],
      });
    }
    // 3) `affectsDomains` referencia dominios reales
    const domainIds = new Set(tpl.scoring.domains.map((d) => d.id));
    tpl.sections.forEach((s, si) => {
      s.questions.forEach((q, qi) => {
        if (q.affectsDomains) {
          q.affectsDomains.forEach((d, di) => {
            if (!domainIds.has(d)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Pregunta "${q.id}" affectsDomains incluye "${d}" inexistente`,
                path: ['sections', si, 'questions', qi, 'affectsDomains', di],
              });
            }
          });
        }
      });
    });
  });

export type SurveyTemplate = z.infer<typeof SurveyTemplateSchema>;

// ─────────────────────────────────────────────────────────────────────
// Helpers de ergonomia
// ─────────────────────────────────────────────────────────────────────

/** Wrapper que retorna un Result en lugar de throw. */
export function parseTemplate(
  input: unknown,
): { ok: true; template: SurveyTemplate } | { ok: false; errors: z.ZodIssue[] } {
  const r = SurveyTemplateSchema.safeParse(input);
  return r.success ? { ok: true, template: r.data } : { ok: false, errors: r.error.issues };
}
